import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useMotionValue } from "framer-motion";
import { toast } from "sonner";
import { Upload, ArrowLeft, Move, Loader2, Type, ImageIcon, Trash2, PenLine } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { api, chf, fileUrl, imgSrc, formatApiErrorDetail } from "../lib/api";
import { useApp } from "../context/AppContext";
import Lightbox from "../components/Lightbox";
import Reviews from "../components/Reviews";
import SEO from "../components/SEO";

const FONTS = [
  { label: "Élégant (Cormorant)", value: '"Cormorant Garamond", serif' },
  { label: "Manuscrit (Great Vibes)", value: '"Great Vibes", cursive' },
  { label: "Chic (Bodoni)", value: '"Bodoni Moda", serif' },
  { label: "Classique (Playfair)", value: '"Playfair Display", serif' },
  { label: "Moderne (Outfit)", value: "Outfit, sans-serif" },
  { label: "Machine (Space Mono)", value: '"Space Mono", monospace' },
];
const SWATCHES = ["#1A1A1A", "#F7F5F0", "#C85A42", "#5C735C", "#2A4C7D", "#B8860B"];
const blankFace = () => ({ text: "", font: FONTS[0].value, color: "#1A1A1A", fontSize: 34, imagePath: null, imgScale: 45 });

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useApp();
  const [product, setProduct] = useState(null);
  const canvasRef = useRef(null);

  const [faceState, setFaceState] = useState({});
  const [activeIndex, setActiveIndex] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [size, setSize] = useState(null);
  const [zoomSrc, setZoomSrc] = useState(null);

  // up to 2 faces × (text, image) draggable positions
  const mv = [
    { text: { x: useMotionValue(0), y: useMotionValue(0) }, image: { x: useMotionValue(0), y: useMotionValue(0) } },
    { text: { x: useMotionValue(0), y: useMotionValue(0) }, image: { x: useMotionValue(0), y: useMotionValue(0) } },
  ];

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => navigate("/catalogue"));
  }, [id, navigate]);

  const faces = useMemo(() => {
    if (!product || !product.customizable) return [];
    return product.faces && product.faces.length ? product.faces.slice(0, 2) : ["Motif"];
  }, [product]);

  useEffect(() => {
    if (faces.length) {
      const init = {};
      faces.forEach((f) => (init[f] = blankFace()));
      setFaceState(init);
      setActiveIndex(0);
      // separate default positions so text and image don't overlap before dragging
      faces.forEach((f, i) => {
        if (mv[i]) {
          mv[i].text.x.set(0); mv[i].text.y.set(0);
          mv[i].image.x.set(0); mv[i].image.y.set(70);
        }
      });
    }
  }, [faces.join("|")]); // eslint-disable-line

  if (!product) {
    return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-terra" /></div>;
  }

  const hasDiscount = product.discount_percent > 0;
  const finalPrice = hasDiscount ? product.price * (1 - product.discount_percent / 100) : product.price;
  const activeFace = faces[activeIndex];
  const fs = faceState[activeFace] || blankFace();
  const isEngrave = product.technique === "Gravure laser";
  const isWrap = product.customizable && (product.wrap_canvas || product.category === "Mug");
  const engraveImgFilter = "grayscale(1) contrast(1.2) brightness(0.85)";

  const setActive = (patch) =>
    setFaceState((s) => ({ ...s, [activeFace]: { ...(s[activeFace] || blankFace()), ...patch } }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setActive({ imagePath: data.path });
      toast.success("Image ajoutée");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const handleAdd = () => {
    if (product.sizes?.length && !size) { toast.error(`Choisissez : ${product.size_label || "taille"}`); return; }
    let customization = null;
    if (product.customizable) {
      const rect = canvasRef.current?.getBoundingClientRect();
      const w = rect?.width || 1, h = rect?.height || 1;
      const sides = {};
      faces.forEach((face, i) => {
        const st = faceState[face] || blankFace();
        const entry = {};
        if (st.text && st.text.trim()) {
          entry.text = {
            value: st.text, font: st.font, color: st.color,
            fontFrac: st.fontSize / h, nx: mv[i].text.x.get() / w, ny: mv[i].text.y.get() / h,
          };
        }
        if (st.imagePath) {
          entry.image = {
            path: st.imagePath, normW: (st.imgScale * 4) / w,
            nx: mv[i].image.x.get() / w, ny: mv[i].image.y.get() / h,
          };
        }
        if (entry.text || entry.image) sides[face] = entry;
      });
      if (Object.keys(sides).length) customization = { sides };
    }
    addToCart({
      productId: product.id,
      name: product.name,
      unitPrice: finalPrice,
      quantity,
      size: size || null,
      sizeLabel: product.size_label || "Taille",
      image_url: product.image_url,
      preview: product.image_url,
      weightGrams: product.weight_grams || 0,
      customization,
    });
    toast.success("Ajouté au panier");
    navigate("/panier");
  };

  return (
    <div className="mx-auto max-w-[1400px] px-5 pt-8 pb-28 md:px-10">
      <SEO title={product.name} path={`/produit/${product.id}`} image={product.image_url} type="product"
        description={`${product.description || product.name} — ${product.customizable ? "à personnaliser avec votre texte ou votre photo" : "pièce réalisée à la main"}, ${chf(finalPrice)}, fait main en Suisse.`} />
      <Link to={product.in_realisations ? "/produits" : "/catalogue"} className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-ink2 hover:text-ink">
        <ArrowLeft className="h-4 w-4" /> {product.in_realisations ? "Produits" : "Catalogue"}
      </Link>

      <div className="mt-8 grid gap-12 lg:grid-cols-[1.1fr_1fr]">
        {/* PREVIEW */}
        <div>
          {isWrap && (
            <>
              <img src={imgSrc(product.image_url)} alt={product.name} onClick={() => setZoomSrc(imgSrc(product.image_url))}
                data-testid="preview-zoom-img" title="Cliquer pour agrandir"
                className="mb-4 aspect-[4/5] w-full cursor-zoom-in border border-line bg-sand object-cover" draggable={false} />
              <p className="mb-2 flex items-center gap-2 text-xs uppercase tracking-widest text-terra" data-testid="wrap-canvas-label">
                <PenLine className="h-3.5 w-3.5" /> C'est ici que vous placez votre texte ou votre image
              </p>
            </>
          )}
          <div ref={canvasRef} className={`relative w-full select-none overflow-hidden border border-line bg-sand ${isWrap ? "aspect-[2/1]" : "aspect-[4/5]"}`} data-testid="customizer-canvas">
            {isWrap ? (
              <>
                <div className="pointer-events-none h-full w-full bg-white" data-testid="mug-wrap-canvas-img" aria-label="Surface d'impression de l'objet représentée à plat" />
                {!fs.imagePath && !(fs.text || "").trim() && (
                  <span className="pointer-events-none absolute inset-0 flex items-center justify-center px-6 text-center text-sm text-ink3/60" data-testid="wrap-empty-hint">
                    Votre texte ou votre image apparaîtra ici
                  </span>
                )}
              </>
            ) : (
              <img src={imgSrc(product.image_url)} alt={product.name} onClick={() => setZoomSrc(imgSrc(product.image_url))}
                data-testid="preview-zoom-img" title="Cliquer pour agrandir"
                className="h-full w-full cursor-zoom-in object-cover" draggable={false} />
            )}

            {product.customizable && faces.map((face, i) => {
              const st = faceState[face] || blankFace();
              return (
                <div key={face} className="pointer-events-none absolute inset-0" style={{ display: i === activeIndex ? "block" : "none" }}>
                  {st.imagePath && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                      <motion.div drag dragConstraints={canvasRef} dragMomentum={false}
                        style={{ x: mv[i].image.x, y: mv[i].image.y }}
                        className="pointer-events-auto cursor-grab active:cursor-grabbing" data-testid={`layer-image-${i}`}>
                        <img src={fileUrl(st.imagePath)} alt="perso" style={{ width: `${st.imgScale * 4}px`, filter: isEngrave ? engraveImgFilter : undefined }} className="pointer-events-none object-contain" draggable={false} />
                      </motion.div>
                    </div>
                  )}
                  {st.text && st.text.trim() && (
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                      <motion.div drag dragConstraints={canvasRef} dragMomentum={false}
                        style={{ x: mv[i].text.x, y: mv[i].text.y }}
                        className="pointer-events-auto cursor-grab active:cursor-grabbing" data-testid={`layer-text-${i}`}>
                        <span style={{ fontFamily: st.font, color: isEngrave ? "#1A1A1A" : st.color, fontSize: `${st.fontSize}px`, lineHeight: 1.1 }} className="max-w-[80vw] px-2 text-center font-medium drop-shadow-sm">
                          {st.text}
                        </span>
                      </motion.div>
                    </div>
                  )}
                </div>
              );
            })}

            {product.customizable && (
              <div className="pointer-events-none absolute bottom-3 left-3 flex items-center gap-2 bg-ink/80 px-3 py-1.5 text-[10px] uppercase tracking-widest text-cream">
                <Move className="h-3 w-3" /> Glissez pour positionner
              </div>
            )}
            {faces.length > 1 && (
              <div className="absolute right-3 top-3 flex overflow-hidden rounded-full border border-line bg-cream/90 text-xs">
                {faces.map((f, i) => (
                  <button key={f} onClick={() => setActiveIndex(i)} data-testid={`face-tab-${f}`}
                    className={`px-3 py-1.5 uppercase tracking-wider transition-colors ${i === activeIndex ? "bg-ink text-cream" : "text-ink2"}`}>
                    {f}
                  </button>
                ))}
              </div>
            )}
          </div>
          <p className="mt-3 text-xs text-ink3" data-testid={isWrap ? "mug-wrap-note" : undefined}>
            {isWrap
              ? "Vue déroulée : cette surface blanche représente l'objet à plat. Le centre correspond à la face avant, les bords gauche et droit se rejoignent à l'arrière. Aperçu indicatif, optimisé par l'atelier avant impression."
              : product.customizable
              ? `Ajoutez du texte et/ou une image, glissez pour positionner. Aperçu indicatif — optimisé par l'atelier avant ${product.technique === "Gravure laser" ? "gravure" : "impression"}.`
              : "Pièce déjà réalisée, prête à commander."}
          </p>
          {product.images?.length > 0 && (
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="product-thumbnails">
              {product.images.map((u, idx) => (
                <button key={idx} onClick={() => setZoomSrc(imgSrc(u))} data-testid={`product-thumb-${idx}`}
                  title="Cliquer pour agrandir"
                  className="aspect-[4/5] overflow-hidden border border-line bg-white transition-transform hover:scale-[1.03]">
                  <img src={imgSrc(u)} alt={`${product.name} — vue ${idx + 2}`} className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* CONTROLS */}
        <div>
          <p className="text-xs uppercase tracking-widest text-ink3">{product.category}{!product.customizable && " · Déjà fait"}</p>
          <h1 className="mt-2 font-serif text-4xl font-medium leading-tight tracking-tight text-ink md:text-5xl">{product.name}</h1>
          <div className="mt-3 flex items-baseline gap-3">
            {hasDiscount && <span className="text-ink3 line-through">{chf(product.price)}</span>}
            <span className="text-2xl font-medium text-ink">{chf(finalPrice)}</span>
            {hasDiscount && <span className="bg-terra px-2 py-0.5 text-xs text-white">-{product.discount_percent}%</span>}
          </div>
          <p className="mt-5 leading-relaxed text-ink2">{product.description}</p>

          {product.sizes?.length > 0 && (
            <div className="mt-6" data-testid="size-selector">
              <label className="mb-2 block text-xs uppercase tracking-widest text-ink3">{product.size_label || "Taille"}</label>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((s) => (
                  <button key={s} onClick={() => setSize(s)} data-testid={`size-${s}`}
                    className={`min-w-[3rem] border px-4 py-2 text-sm transition-colors ${size === s ? "border-ink bg-ink text-cream" : "border-line text-ink2 hover:bg-sand"}`}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.customizable && (
            <div className="mt-8 border border-line bg-cream p-6">
              <div className="mb-4 flex items-center justify-between">
                <p className="font-serif text-xl font-medium text-ink">Personnalisez votre pièce</p>
                {faces.length > 1 && (
                  <div className="flex overflow-hidden rounded-full border border-line text-xs">
                    {faces.map((f, i) => (
                      <button key={f} onClick={() => setActiveIndex(i)} data-testid={`face-btn-${f}`}
                        className={`px-3 py-1.5 uppercase tracking-wider transition-colors ${i === activeIndex ? "bg-ink text-cream" : "text-ink2 hover:bg-sand"}`}>
                        {f}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {faces.length > 1 && <p className="mb-4 -mt-2 text-xs text-ink3">Vous personnalisez : <strong>{activeFace}</strong>. Vous pouvez mettre un motif devant ET derrière.</p>}

              {/* TEXTE */}
              <div className="space-y-4 border-b border-line pb-5">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-ink3"><Type className="h-3.5 w-3.5" /> Texte</div>
                <input value={fs.text} onChange={(e) => setActive({ text: e.target.value })} maxLength={40}
                  placeholder="Votre texte (laisser vide si aucun)" data-testid="customizer-text-input"
                  className="w-full border border-line bg-cream px-4 py-3 text-sm outline-none focus:border-ink" />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-2 block text-[11px] uppercase tracking-widest text-ink3">Police</label>
                    <Select value={fs.font} onValueChange={(v) => setActive({ font: v })}>
                      <SelectTrigger data-testid="customizer-font-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FONTS.map((f) => (<SelectItem key={f.value} value={f.value} style={{ fontFamily: f.value }}>{f.label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="mb-2 block text-[11px] uppercase tracking-widest text-ink3">Taille — {fs.fontSize}px</label>
                    <Slider value={[fs.fontSize]} min={16} max={72} step={1} onValueChange={(v) => setActive({ fontSize: v[0] })} data-testid="customizer-size-slider" />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isEngrave ? (
                    <p className="text-xs text-ink3" data-testid="engrave-mono-note">
                      Gravure monochrome — le rendu apparaît en noir (aperçu « décalqué »), la couleur ne s&apos;applique pas au laser.
                    </p>
                  ) : (
                    <>
                      {SWATCHES.map((s) => (
                        <button key={s} onClick={() => setActive({ color: s })} data-testid={`color-${s}`}
                          className={`h-7 w-7 rounded-full border transition-transform hover:scale-110 ${fs.color === s ? "ring-2 ring-ink ring-offset-2 ring-offset-cream" : "border-line"}`}
                          style={{ backgroundColor: s }} />
                      ))}
                      <input type="color" value={fs.color} onChange={(e) => setActive({ color: e.target.value })} className="h-7 w-9 cursor-pointer border border-line bg-cream" />
                    </>
                  )}
                </div>
              </div>

              {/* IMAGE */}
              <div className="space-y-4 pt-5">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-ink3"><ImageIcon className="h-3.5 w-3.5" /> Image</div>
                {fs.imagePath ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <img src={fileUrl(fs.imagePath)} alt="" style={{ filter: isEngrave ? engraveImgFilter : undefined }} className="h-14 w-14 border border-line object-contain" />
                      <button onClick={() => setActive({ imagePath: null })} data-testid="remove-image-btn" className="flex items-center gap-1 text-xs text-ink2 hover:text-terra"><Trash2 className="h-3.5 w-3.5" /> Retirer</button>
                    </div>
                    <div>
                      <label className="mb-2 block text-[11px] uppercase tracking-widest text-ink3">Taille de l&apos;image</label>
                      <Slider value={[fs.imgScale]} min={20} max={90} step={1} onValueChange={(v) => setActive({ imgScale: v[0] })} data-testid="customizer-imgsize-slider" />
                    </div>
                  </div>
                ) : (
                  <label className="flex cursor-pointer flex-col items-center justify-center gap-2 border border-dashed border-line bg-sand/50 py-6 text-center transition-colors hover:bg-sand" data-testid="customizer-upload-label">
                    {uploading ? <Loader2 className="h-6 w-6 animate-spin text-terra" /> : <Upload className="h-6 w-6 text-ink2" />}
                    <span className="text-sm text-ink2">Téléverser une image (PNG, JPG · 8MB max)</span>
                    <input type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="customizer-file-input" />
                  </label>
                )}
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center border border-line">
              <button onClick={() => setQuantity((q) => Math.max(1, q - 1))} className="px-4 py-3 text-ink2 hover:bg-sand">−</button>
              <span className="w-10 text-center" data-testid="detail-qty">{quantity}</span>
              <button onClick={() => setQuantity((q) => q + 1)} className="px-4 py-3 text-ink2 hover:bg-sand">+</button>
            </div>
            <button onClick={handleAdd} data-testid="add-to-cart-btn"
              className="flex flex-1 items-center justify-center gap-2 bg-ink py-4 text-sm uppercase tracking-widest text-cream transition-colors hover:bg-terra active:scale-[0.98]">
              Ajouter au panier — {chf(finalPrice * quantity)}
            </button>
          </div>

          {[["Matière / support", product.material], ["Dimensions", product.dimensions],
            ["Fabrication", product.production_time], ["Entretien", product.care_instructions]]
            .filter(([, v]) => v && String(v).trim()).length > 0 && (
            <ul className="mt-8 space-y-3 border-t border-line pt-6" data-testid="product-details">
              {[["Matière / support", product.material], ["Dimensions", product.dimensions],
                ["Fabrication", product.production_time], ["Entretien", product.care_instructions]]
                .filter(([, v]) => v && String(v).trim())
                .map(([label, v]) => (
                  <li key={label} className="flex gap-4 text-sm" data-testid={`detail-${label}`}>
                    <span className="w-32 shrink-0 pt-0.5 text-xs uppercase tracking-widest text-ink3">{label}</span>
                    <span className="text-ink2">{v}</span>
                  </li>
                ))}
            </ul>
          )}
        </div>
      </div>

      <Reviews productId={product.id} />

      <AnimatePresence>
        {zoomSrc && <Lightbox src={zoomSrc} alt={product.name} onClose={() => setZoomSrc(null)} />}
      </AnimatePresence>
    </div>
  );
}
